import React, { useState } from 'react';
import { Col, ModalV2, Row, Spinner2, Text } from 'components';
import { Avatar } from 'components/Tailwindcss';
import { theme, Api } from 'lib';
import { format } from 'date-fns';
import qs from 'qs';
import {
  CommentActions,
  CommentBubble,
  CommentBubbleWrapper,
  CommentContent,
  CommentItem,
  CommentMeta,
  ReactionEmoji,
  ReactionsDisplay,
  ReplyItem,
  ViewRepliesButton
} from '../styles';
import { REACTIONS } from '../constants';

const CommentsModal = ({
  visible,
  onClose,
  commentsList,
  loadingComments,
  predictionId,
  onViewReactions
}) => {
  const [expandedReplies, setExpandedReplies] = useState({});
  const [loadingReplies, setLoadingReplies] = useState({});

  const fetchReplies = async (commentId) => {
    if (!predictionId) return;

    if (expandedReplies[commentId]) {
      // Already fetched, just toggle close
      setExpandedReplies((prev) => {
        const newState = { ...prev };
        delete newState[commentId];
        return newState;
      });
      return;
    }

    try {
      setLoadingReplies((prev) => ({ ...prev, [commentId]: true }));

      const response = await Api.get(
        `/admin/predictions/comments/list?${qs.stringify({
          // The endpoint requires predictionItemId — sending predictionId failed
          // validation, so replies never loaded.
          predictionItemId: predictionId,
          replyTo: commentId,
          sortType: 'oldest',
          limit: 50
        })}`
      );

      let replies = [];
      if (Array.isArray(response)) {
        replies = response;
      } else if (response?.comments && Array.isArray(response.comments)) {
        replies = response.comments;
      } else if (response?.data && Array.isArray(response.data)) {
        replies = response.data;
      } else if (response?.data?.comments && Array.isArray(response.data.comments)) {
        replies = response.data.comments;
      }

      if (replies.length > 0) {
        setExpandedReplies((prev) => ({
          ...prev,
          [commentId]: replies
        }));
      }
    } catch (error) {
      console.error('Error fetching replies:', error);
    } finally {
      setLoadingReplies((prev) => {
        const newState = { ...prev };
        delete newState[commentId];
        return newState;
      });
    }
  };

  return (
    <ModalV2
      visible={visible}
      closeModal={onClose}
      width="600px"
      header={
        <Row start centerAll>
          <Text h3 bold>
            Comments List
          </Text>
          {commentsList.length > 0 && (
            <Text b2 med col={theme.GREY_60} marg="0 0 0 12px">
              ({commentsList.length})
            </Text>
          )}
        </Row>
      }
    >
      <Col pad="0" ht="500px" start style={{ overflowY: 'auto', background: '#fff' }}>
        {loadingComments ? (
          <Col center pad="40px 0">
            <Spinner2 />
          </Col>
        ) : commentsList.length === 0 ? (
          <Col center pad="40px 0">
            <Text center b2 col={theme.GREY_60}>
              No comments yet
            </Text>
          </Col>
        ) : (
          commentsList.map((comment) => {
            const reactionCounts = comment.reactionCounts || {};
            const topReactions = Object.entries(reactionCounts)
              .filter(([_, count]) => count > 0)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 3);

            return (
              <React.Fragment key={comment._id}>
                <CommentItem>
                  <Avatar src={comment.owner?.avatar} size={40}>
                    {comment.owner?.name}
                  </Avatar>
                  <CommentContent>
                    <CommentMeta>
                      <Text b2 bold>
                        {comment.owner?.name || `@${comment.owner?.username}`}
                      </Text>
                      <Text c2 col={theme.GREY_60}>
                        •
                      </Text>
                      <Text c2 col={theme.GREY_60}>
                        {comment.createdAt
                          ? format(new Date(comment.createdAt), 'MMM dd, yyyy h:mm a')
                          : ''}
                      </Text>
                    </CommentMeta>
                    <CommentBubbleWrapper>
                      <CommentBubble>
                        <Text
                          b2
                          col={theme.GREY_80}
                          style={{ wordBreak: 'break-word', lineHeight: '1.5' }}
                        >
                          {comment.body}
                        </Text>
                      </CommentBubble>
                      {topReactions.length > 0 && (
                        <ReactionsDisplay onClick={() => onViewReactions(comment._id)}>
                          {topReactions.map(([reactionType, count]) => (
                            <ReactionEmoji
                              key={reactionType}
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewReactions(comment._id, reactionType);
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              {REACTIONS[reactionType]}
                            </ReactionEmoji>
                          ))}
                          <Text c2 col={theme.GREY_60} style={{ marginLeft: '2px' }}>
                            {comment.reactionCount || 0}
                          </Text>
                        </ReactionsDisplay>
                      )}
                    </CommentBubbleWrapper>
                    {comment.replies?.length > 0 && (
                      <CommentActions>
                        <ViewRepliesButton onClick={() => fetchReplies(comment._id)}>
                          {loadingReplies[comment._id] ? (
                            <Text c2 bold>
                              Loading...
                            </Text>
                          ) : expandedReplies[comment._id] ? (
                            <Text c2 bold>
                              Hide {comment.replies.length}{' '}
                              {comment.replies.length === 1 ? 'Reply' : 'Replies'}
                            </Text>
                          ) : (
                            <Text c2 bold>
                              View {comment.replies.length}{' '}
                              {comment.replies.length === 1 ? 'Reply' : 'Replies'}
                            </Text>
                          )}
                        </ViewRepliesButton>
                      </CommentActions>
                    )}
                  </CommentContent>
                </CommentItem>
                {/* Render expanded replies */}
                {expandedReplies[comment._id] &&
                  expandedReplies[comment._id].length > 0 &&
                  expandedReplies[comment._id].map((reply) => {
                    const replyReactionCounts = reply.reactionCounts || {};
                    const replyTopReactions = Object.entries(replyReactionCounts)
                      .filter(([_, count]) => count > 0)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 3);

                    return (
                      <ReplyItem key={reply._id}>
                        <Avatar src={reply.owner?.avatar} size={32}>
                          {reply.owner?.name}
                        </Avatar>
                        <CommentContent>
                          <CommentMeta>
                            <Text b2 bold>
                              {reply.owner?.name || `@${reply.owner?.username}`}
                            </Text>
                            <Text c2 col={theme.GREY_60}>
                              •
                            </Text>
                            <Text c2 col={theme.GREY_60}>
                              {reply.createdAt
                                ? format(new Date(reply.createdAt), 'MMM dd, yyyy h:mm a')
                                : ''}
                            </Text>
                          </CommentMeta>
                          <CommentBubbleWrapper>
                            <CommentBubble>
                              <Text
                                b2
                                col={theme.GREY_80}
                                style={{ wordBreak: 'break-word', lineHeight: '1.5' }}
                              >
                                {reply.body}
                              </Text>
                            </CommentBubble>
                            {replyTopReactions.length > 0 && (
                              <ReactionsDisplay onClick={() => onViewReactions(reply._id)}>
                                {replyTopReactions.map(([reactionType, count]) => (
                                  <ReactionEmoji
                                    key={reactionType}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onViewReactions(reply._id, reactionType);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    {REACTIONS[reactionType]}
                                  </ReactionEmoji>
                                ))}
                                <Text c2 col={theme.GREY_60} style={{ marginLeft: '2px' }}>
                                  {reply.reactionCount || 0}
                                </Text>
                              </ReactionsDisplay>
                            )}
                          </CommentBubbleWrapper>
                        </CommentContent>
                      </ReplyItem>
                    );
                  })}
              </React.Fragment>
            );
          })
        )}
      </Col>
    </ModalV2>
  );
};

export default CommentsModal;
